import React, { useRef, useState } from 'react';
import { Button } from './ui/button';
import { Paperclip, X, FileImage, FileVideo, FileAudio, FileText, File } from 'lucide-react';
import { toast } from 'sonner';

// Tipos de arquivos permitidos pelo WhatsApp
// Limite máximo: 14MB para todos os tipos
const ALLOWED_FILE_TYPES = {
  // Imagens
  'image/jpeg': { ext: 'jpg', maxSize: 14 * 1024 * 1024, category: 'image' },
  'image/png': { ext: 'png', maxSize: 14 * 1024 * 1024, category: 'image' },
  'image/gif': { ext: 'gif', maxSize: 14 * 1024 * 1024, category: 'image' },
  'image/webp': { ext: 'webp', maxSize: 14 * 1024 * 1024, category: 'image' },
  // Vídeos
  'video/mp4': { ext: 'mp4', maxSize: 14 * 1024 * 1024, category: 'video' },
  'video/3gpp': { ext: '3gp', maxSize: 14 * 1024 * 1024, category: 'video' },
  // Áudio
  'audio/ogg': { ext: 'ogg', maxSize: 14 * 1024 * 1024, category: 'audio' },
  'audio/amr': { ext: 'amr', maxSize: 14 * 1024 * 1024, category: 'audio' },
  'audio/wav': { ext: 'wav', maxSize: 14 * 1024 * 1024, category: 'audio' },
  'audio/mpeg': { ext: 'mp3', maxSize: 14 * 1024 * 1024, category: 'audio' },
  'audio/aac': { ext: 'aac', maxSize: 14 * 1024 * 1024, category: 'audio' },
  'audio/mp4': { ext: 'm4a', maxSize: 14 * 1024 * 1024, category: 'audio' },
  // Documentos
  'application/pdf': { ext: 'pdf', maxSize: 14 * 1024 * 1024, category: 'document' },
  'application/msword': { ext: 'doc', maxSize: 14 * 1024 * 1024, category: 'document' },
  'application/vnd.openxmlformats-officedocument.wordprocessingml.document': { ext: 'docx', maxSize: 14 * 1024 * 1024, category: 'document' },
  'application/vnd.ms-excel': { ext: 'xls', maxSize: 14 * 1024 * 1024, category: 'document' },
  'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet': { ext: 'xlsx', maxSize: 14 * 1024 * 1024, category: 'document' },
  'application/vnd.ms-powerpoint': { ext: 'ppt', maxSize: 14 * 1024 * 1024, category: 'document' },
};

const formatFileSize = (bytes) => {
  if (bytes < 1024) return bytes + ' B';
  if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + ' KB';
  return (bytes / (1024 * 1024)).toFixed(1) + ' MB';
};

const getFileIcon = (category) => {
  switch (category) {
    case 'image':
      return <FileImage className="w-4 h-4 text-blue-500" />;
    case 'video':
      return <FileVideo className="w-4 h-4 text-purple-500" />;
    case 'audio':
      return <FileAudio className="w-4 h-4 text-teal-500" />;
    case 'document':
      return <FileText className="w-4 h-4 text-orange-500" />;
    default:
      return <File className="w-4 h-4 text-gray-500" />;
  }
};

export default function FileAttachment({ 
  onFileSelect, 
  selectedFile, 
  onRemove, 
  disabled = false
}) {
  const fileInputRef = useRef(null);
  const [dragOver, setDragOver] = useState(false);

  const validateFile = (file) => {
    const fileType = ALLOWED_FILE_TYPES[file.type];
    
    if (!fileType) {
      toast.error('Tipo de arquivo não permitido pelo WhatsApp');
      return false;
    }

    if (file.size > fileType.maxSize) {
      const maxSizeMB = fileType.maxSize / (1024 * 1024);
      toast.error(`Arquivo muito grande. Máximo: ${maxSizeMB}MB para ${fileType.category === 'document' ? 'documentos' : 'mídia'}`);
      return false;
    }

    return fileType;
  };

  const handleFileChange = (e) => {
    const file = e.target.files?.[0];
    if (file) {
      const fileType = validateFile(file);
      if (fileType) {
        onFileSelect({
          file,
          name: file.name,
          size: file.size,
          type: file.type,
          category: fileType.category,
        });
      }
    }
    // Reset input value so same file can be selected again
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const handleDrop = (e) => {
    e.preventDefault();
    setDragOver(false);
    
    const file = e.dataTransfer.files?.[0];
    if (file) {
      const fileType = validateFile(file);
      if (fileType) {
        onFileSelect({
          file,
          name: file.name,
          size: file.size,
          type: file.type,
          category: fileType.category,
        });
      }
    }
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    setDragOver(true);
  };

  const handleDragLeave = (e) => {
    e.preventDefault();
    setDragOver(false);
  };

  return (
    <div className="space-y-2 max-w-full">
      {/* Botão de anexar */}
      {!selectedFile && (
        <div
          className={`border-2 border-dashed rounded-lg p-3 transition-colors ${
            dragOver ? 'border-primary bg-primary/5' : 'border-muted-foreground/25'
          } ${disabled ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer hover:border-primary/50'}`}
          onClick={() => !disabled && fileInputRef.current?.click()}
          onDrop={handleDrop}
          onDragOver={handleDragOver}
          onDragLeave={handleDragLeave}
        >
          <input
            ref={fileInputRef}
            type="file"
            className="hidden"
            onChange={handleFileChange}
            accept={Object.keys(ALLOWED_FILE_TYPES).join(',')}
            disabled={disabled}
          />
          <div className="flex items-center justify-center gap-2 text-sm text-muted-foreground">
            <Paperclip className="w-4 h-4" />
            <span>Anexar arquivo</span>
          </div>
          <p className="text-[10px] text-center text-muted-foreground mt-1">
            Imagens, vídeos, áudios e documentos (máx. 14MB)
          </p>
        </div>
      )}

      {/* Preview do arquivo selecionado */}
      {selectedFile && (
        <div className="flex items-center gap-3 bg-muted/50 rounded-lg p-3 w-full" style={{ maxWidth: '100%' }}>
          {/* Thumbnail para imagens (apenas novos arquivos) */}
          {selectedFile.category === 'image' && selectedFile.file && (
            <div className="w-12 h-12 rounded overflow-hidden flex-shrink-0">
              <img
                src={URL.createObjectURL(selectedFile.file)}
                alt="Preview"
                className="w-full h-full object-cover"
              />
            </div>
          )}
          
          {/* Ícone para imagens existentes ou outros tipos */}
          {(selectedFile.category !== 'image' || selectedFile.existingAttachment) && (
            <div className="w-10 h-10 rounded bg-muted flex items-center justify-center flex-shrink-0">
              {getFileIcon(selectedFile.category)}
            </div>
          )}

          {/* Info do arquivo - usando width:0 + flex-grow para forçar truncamento */}
          <div className="flex-1 overflow-hidden" style={{ minWidth: 0, width: 0 }}>
            <p className="text-sm font-medium truncate" title={selectedFile.name}>{selectedFile.name}</p>
            <p className="text-xs text-muted-foreground truncate">
              {formatFileSize(selectedFile.size)} • {selectedFile.category}
              {selectedFile.existingAttachment && ' • Anexo existente'}
            </p>
          </div>

          {/* Botão remover */}
          <Button
            type="button"
            variant="ghost"
            size="icon"
            className="h-8 w-8 flex-shrink-0"
            onClick={onRemove}
            disabled={disabled}
          >
            <X className="w-4 h-4" />
          </Button>
        </div>
      )}
    </div>
  );
}
